import { Goal, ID } from '@griply/types';

import { getGoalAncestors } from './get-goal-ancestors';

export const isGoalArchived = (goal: Goal, goals: Goal[]): boolean => {
  if (goal.archivedAt) {
    return true;
  }
  return getGoalAncestors(goal, goals).some(
    (ancestor) => !!ancestor.archivedAt,
  );
};

// Ancestors are looked up inside `goals` — pass the full set; a filtered subset breaks the chain and orphans resolve to null.
const resolveInheritedValues = <T>(
  goals: Goal[],
  getOwnValue: (goal: Goal) => T | null,
): Map<ID, T | null> => {
  const goalsMap = new Map(goals.map((goal) => [goal.id, goal]));
  const resolved = new Map<ID, T | null>();

  const resolve = (goal: Goal, visited: Set<ID>): T | null => {
    const cached = resolved.get(goal.id);
    if (cached !== undefined) {
      return cached;
    }
    const ownValue = getOwnValue(goal);
    if (ownValue !== null) {
      resolved.set(goal.id, ownValue);
      return ownValue;
    }
    const parentId = goal.parentIds?.[goal.parentIds.length - 1];
    const parent = parentId ? goalsMap.get(parentId) : undefined;
    if (!parent || visited.has(parent.id)) {
      resolved.set(goal.id, null);
      return null;
    }
    visited.add(parent.id);
    const result = resolve(parent, visited);
    resolved.set(goal.id, result);
    return result;
  };

  goals.forEach((goal) => resolve(goal, new Set<ID>([goal.id])));
  return resolved;
};

const collectGoalIdsWithInheritedFlag = (
  goals: Goal[],
  hasFlag: (goal: Goal) => boolean,
): Set<ID> => {
  const resolved = resolveInheritedValues(goals, (goal) =>
    hasFlag(goal) ? true : null,
  );

  const flaggedIds = new Set<ID>();
  resolved.forEach((isFlagged, id) => {
    if (isFlagged) {
      flaggedIds.add(id);
    }
  });
  return flaggedIds;
};

export const getArchivedGoalIds = (goals: Goal[]): Set<ID> =>
  collectGoalIdsWithInheritedFlag(goals, (goal) => !!goal.archivedAt);

export const getCompletedGoalIds = (goals: Goal[]): Set<ID> =>
  collectGoalIdsWithInheritedFlag(goals, (goal) => !!goal.completedAt);

// update-goal nulls a sub-goal's lifeAreaId, so its life area only exists on an ancestor.
export const getInheritedLifeAreaIds = (goals: Goal[]): Map<ID, ID | null> =>
  resolveInheritedValues(goals, (goal) => goal.lifeAreaId ?? null);
